import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { Layout } from "views/common/Layout";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "views/components/ui/Card";
import { Loader2, ArrowRight, Heart } from "lucide-react";
import { fetchTool, fetchAlternativeTools } from "actions/tool";

// Helper function to validate URL
const isValidUrl = (urlString) => {
    if (!urlString) return false;
    try {
        const url = new URL(urlString);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (e) {
        return false;
    }
};

const AlternativeToolsPage = (props) => {
    const { toolName } = useParams();
    const history = useHistory();

    useEffect(() => {
        if (toolName) {
            props.fetchTool(toolName);
        }
    }, [toolName]);

    useEffect(() => {
        if (props.tool?.uuid) {
            props.fetchAlternativeTools(props.tool.uuid, 20);
        }
    }, [props.tool?.uuid]);

    const handleToolClick = (toolName) => {
        history.push(`/tool/${toolName}`);
    };

    if (props.isLoading || !props.tool) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center min-h-[50vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </Layout>
        );
    }

    const { tool, alternativeTools } = props;
    const toolNames = alternativeTools.map((tool) => tool.name).join(", ");

    return (
        <Layout>
            <Helmet>
                <title>{`${tool.name} Alternatives - Similar AI Tools | Launch Hero FYI`}</title>
                <meta
                    name="description"
                    content={`Discover the best alternatives to ${tool.name}. Browse through ${alternativeTools.length} curated alternative tools including ${toolNames}.`}
                />
                <meta
                    name="keywords"
                    content={`${tool.name} alternatives, ${toolNames}, AI tools, software solutions`}
                />
                <meta
                    property="og:title"
                    content={`${tool.name} Alternatives - Similar AI Tools | Launch Hero FYI`}
                />
                <meta
                    property="og:description"
                    content={`Discover the best alternatives to ${tool.name}. Browse through ${alternativeTools.length} curated alternative tools including ${toolNames}.`}
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary" />
                <meta
                    name="twitter:title"
                    content={`${tool.name} Alternatives - Similar AI Tools | Launch Hero FYI`}
                />
                <meta
                    name="twitter:description"
                    content={`Discover the best alternatives to ${tool.name}. Browse through ${alternativeTools.length} curated alternative tools including ${toolNames}.`}
                />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">
                        <button
                            onClick={() => handleToolClick(tool.name)}
                            className="text-blue-600 hover:text-blue-700 transition-colors"
                        >
                            {tool.name}
                        </button>{" "}
                        Alternatives
                    </h1>
                    <p className="mt-2 text-muted-foreground">
                        Discover similar tools and alternatives to {tool.name}.
                        These tools offer comparable features and functionality.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-3 space-y-6">
                        {/* Tools Grid */}
                        <div>
                            <div>
                                <div className="text-xl font-semibold text-gray-900 mb-6">
                                    Alternative Tools ({alternativeTools.length}
                                    )
                                </div>
                            </div>
                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {alternativeTools.map((altTool) => (
                                        <div
                                            key={altTool.uuid}
                                            className="group p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                            onClick={() =>
                                                handleToolClick(altTool.name)
                                            }
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <img
                                                    src={
                                                        altTool.logo_url &&
                                                        isValidUrl(
                                                            altTool.logo_url
                                                        )
                                                            ? altTool.logo_url
                                                            : altTool.thumbnail ||
                                                              "https://via.placeholder.com/48"
                                                    }
                                                    alt={altTool.name}
                                                    className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                                                    onError={(e) => {
                                                        e.target.src =
                                                            altTool.thumbnail ||
                                                            "https://via.placeholder.com/48";
                                                    }}
                                                />
                                                <div>
                                                    <h3 className="font-medium group-hover:text-blue-600 transition-colors">
                                                        {altTool.name}
                                                    </h3>
                                                    <p className="text-sm text-gray-500">
                                                        {altTool.company}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {altTool.shortDescription}
                                            </p>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                                    <ArrowRight className="h-4 w-4 opacity-100 group-hover:opacity-100 transition-opacity" />
                                                    <span>View details</span>
                                                </div>
                                                <button
                                                    className="text-gray-500 hover:text-red-500"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <Heart className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Features Comparison */}
                        {tool.features && tool.features.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Key Features</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {tool.features.map((feature, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg"
                                            >
                                                <span className="text-sm">
                                                    {feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Use Cases */}
                        {tool.useCases && tool.useCases.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Common Use Cases</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {tool.useCases.map((useCase, index) => (
                                            <div
                                                key={index}
                                                className="flex flex-col gap-2 p-4 bg-gray-50 rounded-lg"
                                            >
                                                {typeof useCase === "string" ? (
                                                    <span className="text-sm">
                                                        {useCase}
                                                    </span>
                                                ) : (
                                                    <>
                                                        <h4 className="font-medium text-sm">
                                                            {useCase.title}
                                                        </h4>
                                                        <p className="text-sm text-gray-600">
                                                            {
                                                                useCase.description
                                                            }
                                                        </p>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const mapStateToProps = (state, ownProps) => {
    const { toolName } = ownProps.match.params;
    return {
        tool: state.tool.toolsMap[toolName],
        alternativeTools: state.tool.alternativeTools,
        isLoading: state.tool.isLoading,
    };
};

export default connect(mapStateToProps, { fetchTool, fetchAlternativeTools })(
    AlternativeToolsPage
);
